import {Component, EventEmitter, Input, Output} from '@angular/core';
import {CommonModule} from "@angular/common";
import {RouterModule} from '@angular/router';

@Component({
  selector: 'ui-button',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './ui-button.component.html',
  styleUrl: './ui-button.component.scss'
})
export class UiButtonComponent {

  /**
   * @deprecated UiButton is always DaisyUI-styled in this repo.
   * Kept for backwards compatibility with existing templates.
   */
  @Input() mode: 'tw' | 'daisy' = 'daisy';

  @Input() variant: 'primary' | 'secondary' | 'danger' | 'warning' | 'success' | 'accent' | 'neutral' | 'ghost' = 'primary';
  @Input() outline = false;
  @Input() size: 'sm' | 'md' | 'lg' | 'wide' | 'auto' = 'sm';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() fullWidth = false;
  /** Full width on small screens, but keep desktop sizing natural. */
  @Input() fullWidthOnMobile = false;
  @Input() className = '';
  @Input() appearance: 'default' | 'icon' = 'default';

  /**
   * Controls background/visual treatment independent of appearance.
   * - transparent: no bg, subtle hover (ghost-like)
   * - soft: your current default
   * - solid: filled
   * - outline: bordered
   * - link: text link
   */
  @Input() tone: 'transparent' | 'soft' | 'solid' | 'outline' | 'link' = 'solid';

  /**
   * If set, the component renders an <a> tag instead of a <button>.
   * Use for navigation.
   */
  @Input() href?: string | null;

  /**
   * Angular router link. If set, renders an <a> tag with [routerLink].
   * Takes precedence over href.
   */
  @Input() routerLink?: any[] | string | null;

  /**
   * Query params for routerLink
   */
  @Input() queryParams?: any;

  /**
   * Fragment for routerLink (hash)
   */
  @Input() fragment?: string;

  /**
   * Only applies when rendering as a link.
   * Example: "_blank".
   */
  @Input() target?: string | null;

  /**
   * Only applies when rendering as a link.
   * If target is _blank and rel isn't provided, we'll default to "noopener noreferrer".
   */
  @Input() rel?: string | null;

  @Output() clicked = new EventEmitter<Event>();

  onClick(event: Event) {
    if (this.disabled || this.loading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.clicked.emit(event);
  }

  get isLink(): boolean {
    return !!(this.routerLink || this.href);
  }

  get isRouterLink(): boolean {
    return !!this.routerLink;
  }

  get ariaDisabled(): 'true' | null {
    return (this.disabled || this.loading) ? 'true' : null;
  }

  get computedTabIndex(): number | null {
    // Anchors don't support a disabled attribute; remove from tab order when "disabled".
    return (this.disabled || this.loading) ? -1 : null;
  }

  get computedRel(): string | null {
    if (this.rel) return this.rel;
    if (this.target === '_blank') return 'noopener noreferrer';
    return null;
  }

  private get baseClasses(): string[] {
    return ['btn'];
  }

  private get appearanceClasses(): string[] {
    if (this.appearance === 'icon') {
      // shape only
      const sizeMap = { sm: 'h-8 w-8', md: 'h-10 w-10', lg: 'h-12 w-12' } as const;
      return ['btn-square', sizeMap[this.size], 'p-0'];
    }
    return []; // default uses daisy sizing
  }

  private get toneClasses(): string[] {
    // map visual treatment (independent from icon/default)
    switch (this.tone) {
      case 'transparent':
        return ['btn-ghost']; // no bg except hover
      case 'soft':
        return ['btn-soft', this.daisyVariantClasses];
      case 'solid':
        return [this.daisyVariantClasses]; // filled by default in daisyUI
      case 'outline':
        return ['btn-outline', this.daisyVariantClasses];
      case 'link':
        return ['btn-link', this.linkToneVariantClass()];
      default:
        return ['btn-soft', this.daisyVariantClasses];
    }
  }

  // optional: for link tone, you might prefer text color rather than button variant
  private linkToneVariantClass(): string {
    const map = {
      primary: 'link-primary',
      secondary: 'link-secondary',
      danger: 'link-error',
      warning: 'link-warning',
      success: 'link-success',
      accent: 'link-accent',
      neutral: 'link-neutral',
      ghost: '', // default
    } as const;
    return map[this.variant] ?? '';
  }

  private get sizeClasses(): string[] {
    // only apply btn-sm etc for non-icon appearance (icon uses h/w sizing already)
    if (this.appearance === 'icon') return [];

    // `auto` means: fill available space (width), but keep default button height.
    // This plays nicely with responsive containers like the subheader action-bar grid.
    if (this.size === 'auto') return ['w-full'];

    const sizes = { sm: 'btn-sm', md: '', lg: 'btn-lg' , wide: 'btn-wide'} as const;
    return [sizes[this.size as Exclude<typeof this.size, 'auto'>]].filter(Boolean);
  }


  // DaisyUI (no prefix) -------------------------------------------------------
  private get daisyVariantClasses(): string {
    const variants = {
      primary: 'btn-primary',
      secondary: 'btn-secondary',
      danger: 'btn-error',
      ghost: 'btn-ghost',
      success: 'btn-success',
      accent: 'btn-accent',
      neutral: 'btn-neutral',
      warning: 'btn-warning',
    };
    return variants[this.variant];
  }

  get computedClasses(): string {
    return [
      ...this.baseClasses,
      ...this.toneClasses,
      ...this.appearanceClasses,
      ...this.sizeClasses,
      this.fullWidth ? 'w-full' : '',
      this.fullWidthOnMobile ? 'w-full lg:w-auto' : '',
      this.className
    ].filter(Boolean).join(' ');
  }
}
